"use client";

import { useEffect, useState } from "react";

/**
 * The countdown that takes a visitor from the farewell page to the new address.
 *
 * ⚠️ **Only ever mounted on `/`.** Every other path redirects before this page
 * renders at all (`legacy-answer.ts`), so this is never a delay in front of
 * somebody who asked for a specific page.
 *
 * ⚠️ **`seconds === 0` renders nothing and navigates never**, which is a real
 * configured state rather than a disabled feature: it is what a merchant checking
 * their own page over needs, and it is the default on the closure notice, where
 * pushing a visitor onto a marketing site they never asked for would be an ad
 * rather than a redirect.
 *
 * `location.replace` rather than `assign`: the old address must not become a
 * back-button trap that bounces the visitor straight forward again.
 */
export function AutoAdvance({
	seconds,
	label,
	href,
}: {
	seconds: number;
	/** Carries `{{seconds}}`, substituted here so the sentence stays one translated string. */
	label: string;
	href: string;
}) {
	const [left, setLeft] = useState(seconds);

	useEffect(() => {
		if (seconds <= 0) return;
		if (left <= 0) {
			globalThis.location.replace(href);
			return;
		}
		const timer = setTimeout(() => setLeft((n) => n - 1), 1000);
		return () => clearTimeout(timer);
	}, [left, seconds, href]);

	if (seconds <= 0 || !label) return null;

	return (
		<p className="text-sm text-muted-foreground" aria-live="polite">
			{/* `replaceAll`: a translated sentence may name the token twice, and
			    `replace` would leave the second one rendered verbatim. */}
			{label.replaceAll("{{seconds}}", String(Math.max(left, 0)))}
		</p>
	);
}
